// ABOUTME: Dialog for assigning a node to a user (owner), a dealership, or a client via a node picker.
// ABOUTME: Searches the target hierarchy (dealerships: the shared DealershipSelect, only those the user may assign) and calls the assign endpoint;
// ABOUTME: when the object schema asks before propagating to linked nodes, it shows a confirmation step.

import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@ds/coolshop';
import { SearchFilterOperator } from '@src/__generated__/types';
import { useCompactLinkSearchLazyQuery } from '@src/components/formFields/compactLink.generated';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { fetchSelectableDealerships } from '../../dealership/api/dealershipApi';
import { DealershipSelect } from '../../dealership/components/DealershipSelect';
import type { SelectableDealerships } from '../../dealership/types';
import { AssignmentPropagationGroup, assignNode, AssignTarget } from '../api/assignmentApi';
import { describeAssignError } from '../utils/describeAssignError';

type AssignOption = {
    id: string;
    code: string;
    listName: string;
};

type AssignNodeDialogProps = {
    open: boolean;
    hierarchyId: string;
    nodeId: string;
    target: AssignTarget;
    onClose: () => void;
    onAssigned: () => void;
};

export function AssignNodeDialog({
    open,
    hierarchyId,
    nodeId,
    target,
    onClose,
    onAssigned,
}: AssignNodeDialogProps) {
    const { formatMessage } = useIntl();
    const settings = useContext(SettingsContext);
    const [searchInput, setSearchInput] = useState('');
    const [options, setOptions] = useState<AssignOption[]>([]);
    // The dealerships the user may assign (their user_info.dealership links, or every dealership for
    // the all-dealership roles); the server refuses any other one. A node is assigned to a dealership,
    // so "none" is not one of them.
    const [assignableDealerships, setAssignableDealerships] = useState<SelectableDealerships>({
        dealerships: [],
        allowNone: false,
    });
    const [selected, setSelected] = useState<AssignOption | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Set when the object schema asks whether the assignment also goes to the linked nodes.
    const [linkedNodes, setLinkedNodes] = useState<AssignmentPropagationGroup[] | null>(null);

    // owner and client both pick a user-management node (a client is a user); dealership picks a
    // dealership node.
    const targetHierarchyId =
        target === 'dealership'
            ? settings?.userManagement?.dealershipHierarchyId
            : settings?.userManagement?.hierarchyId;

    const [executeSearch] = useCompactLinkSearchLazyQuery();

    // Reset transient state whenever the dialog opens or the target changes.
    useEffect(() => {
        if (!open) return;
        setSearchInput('');
        setOptions([]);
        setSelected(null);
        setError(null);
        setLinkedNodes(null);
    }, [open, target]);

    useEffect(() => {
        if (!open || target !== 'dealership') return;
        fetchSelectableDealerships()
            .then(({ dealerships }) => setAssignableDealerships({ dealerships, allowNone: false }))
            .catch(() => setAssignableDealerships({ dealerships: [], allowNone: false }));
    }, [open, target]);

    useEffect(() => {
        if (!open || !targetHierarchyId || target === 'dealership' || searchInput.length < 2) {
            return undefined;
        }
        const timer = setTimeout(() => {
            executeSearch({
                variables: {
                    ids: [targetHierarchyId],
                    filter: [
                        {
                            field: 'code',
                            operator: SearchFilterOperator.Contains,
                            value: [searchInput],
                        },
                    ],
                    sort: [],
                },
            }).then(result => {
                const nodes = result.data?.searchHierarchies?.nodes?.edges ?? [];
                setOptions(
                    nodes.map(e => ({
                        id: e.node.id,
                        code: e.node.code,
                        listName: e.node.listName ?? e.node.code,
                    })),
                );
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [open, searchInput, target, targetHierarchyId, executeSearch]);

    const title = useMemo(() => {
        if (target === 'dealership') {
            return formatMessage({
                id: 'nodeAssignment.title-dealership',
                defaultMessage: 'Assign to Dealership',
            });
        }
        if (target === 'client') {
            return formatMessage({
                id: 'nodeAssignment.title-client',
                defaultMessage: 'Assign to Client',
            });
        }
        return formatMessage({
            id: 'nodeAssignment.title-owner',
            defaultMessage: 'Assign to User',
        });
    }, [target, formatMessage]);

    const handleAssign = useCallback(
        async (propagate?: boolean) => {
            if (!selected) return;
            setLoading(true);
            setError(null);
            try {
                const result = await assignNode(
                    hierarchyId,
                    nodeId,
                    target,
                    selected.id,
                    propagate,
                );
                if (result.choiceRequired) {
                    setLinkedNodes(result.linkedNodes);
                    return;
                }
                onAssigned();
            } catch (err) {
                setError(describeAssignError(err, formatMessage));
            } finally {
                setLoading(false);
            }
        },
        [selected, hierarchyId, nodeId, target, onAssigned, formatMessage],
    );

    const linkedNodesTotal = linkedNodes?.reduce((sum, group) => sum + group.nodes.length, 0) ?? 0;

    if (linkedNodes) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Stack gap={2} paddingTop={1}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Typography>
                            {formatMessage(
                                {
                                    id: 'nodeAssignment.propagation-question',
                                    defaultMessage:
                                        '{count, plural, one {# linked node} other {# linked nodes}} can receive the same assignment. Assign them too?',
                                },
                                { count: linkedNodesTotal },
                            )}
                        </Typography>
                        {linkedNodes.map(group => (
                            <Stack key={group.hierarchyId} gap={0.5}>
                                <Typography variant="subtitle2">
                                    {`${group.hierarchyCode} (${group.nodes.length})`}
                                </Typography>
                                {group.nodes.map(node => (
                                    <Typography
                                        key={node.nodeId}
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {node.listName && node.listName !== node.code
                                            ? `${node.code} - ${node.listName}`
                                            : node.code}
                                    </Typography>
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={onClose} disabled={loading}>
                        {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button variant="text" onClick={() => handleAssign(false)} disabled={loading}>
                        {formatMessage({
                            id: 'nodeAssignment.assign-node-only',
                            defaultMessage: 'Assign this node only',
                        })}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleAssign(true)}
                        disabled={loading}
                    >
                        {formatMessage({
                            id: 'nodeAssignment.assign-linked-nodes-too',
                            defaultMessage: 'Assign linked nodes too',
                        })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            data-testid="assign-node-dialog"
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Stack gap={2} paddingTop={1}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {!targetHierarchyId && (
                        <Alert severity="info">
                            {formatMessage({
                                id: 'nodeAssignment.no-hierarchy',
                                defaultMessage:
                                    'The target hierarchy is not configured. Initialize user management and dealerships first.',
                            })}
                        </Alert>
                    )}
                    {target === 'dealership' ? (
                        <DealershipSelect
                            fullWidth
                            selectable={assignableDealerships}
                            label={formatMessage({
                                id: 'dealership.picker.label',
                                defaultMessage: 'Dealership',
                            })}
                            value={selected?.id}
                            onChange={dealershipId => {
                                const dealership = assignableDealerships.dealerships.find(
                                    d => d.id === dealershipId,
                                );
                                setSelected(
                                    dealership
                                        ? {
                                              id: dealership.id,
                                              code: dealership.code,
                                              listName: dealership.name,
                                          }
                                        : null,
                                );
                            }}
                            disabled={!targetHierarchyId}
                        />
                    ) : (
                        <Autocomplete
                            label={formatMessage({
                                id: 'nodeAssignment.select',
                                defaultMessage: 'Select',
                            })}
                            options={options}
                            value={selected}
                            getOptionLabel={(o: AssignOption) =>
                                o.listName ? `${o.code} - ${o.listName}` : o.code
                            }
                            isOptionEqualToValue={(option: AssignOption, val: AssignOption) =>
                                option.id === val.id
                            }
                            filterOptions={(x: AssignOption[]) => x}
                            onChange={(_e: React.SyntheticEvent, value: AssignOption | null) =>
                                setSelected(value)
                            }
                            onInputChange={(_e: React.SyntheticEvent, val: string) =>
                                setSearchInput(val)
                            }
                            disabled={!targetHierarchyId}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={onClose} disabled={loading}>
                    {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleAssign()}
                    disabled={loading || !selected}
                    data-testid="assign-node-confirm"
                >
                    {loading
                        ? formatMessage({
                              id: 'nodeAssignment.assigning',
                              defaultMessage: 'Assigning...',
                          })
                        : formatMessage({ id: 'nodeAssignment.assign', defaultMessage: 'Assign' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
